<template>
  <v-card ref="resizeHost" outlined light color="white" class="chart-card fill-height">
    <v-card-title class="caption font-weight-medium pa-0 chart-title">
      Tendencias últimos 7 días
    </v-card-title>
    <v-card-text class="pa-0 chart-body">
      <template v-if="loading">
        <div class="chart-loading-shell">
          <v-skeleton-loader type="image" height="100%" class="chart-skeleton-fill"></v-skeleton-loader>
        </div>
      </template>
      <template v-else-if="noData">
        <div class="text-center py-5 caption">
          No hay datos de tendencias
        </div>
      </template>
      <template v-else>
        <div ref="chartTrends" class="range-trend-chart__canvas"></div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'DashboardRangeTrendChart',
  props: {
    statsData: {
      type: Object,
      default: null
    },
    loading: {
        type: Boolean,
        default: false
    }
  },
  data() {
    return {
      noData: true,
      chartInstance: null,
      resizeObserver: null,
      resizeTimer: null
    }
  },
  watch: {
    statsData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.processData(val)
          })
        }
      }
    },
    loading(val) {
      if (!val) {
        this.scheduleChartResize()
      }
    }
  },
  beforeDestroy() {
    this.teardownResizeObserver()
    clearTimeout(this.resizeTimer)
    this.resizeTimer = null
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  mounted() {
    this.handleResize = () => {
      this.scheduleChartResize()
    }
    window.addEventListener('resize', this.handleResize)
    this.setupResizeObserver()
    this.scheduleChartResize()
  },
  methods: {
    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },

    scheduleChartResize() {
      clearTimeout(this.resizeTimer)
      this.$nextTick(() => {
        this.resizeChart()
        this.resizeTimer = setTimeout(() => {
          this.resizeChart()
        }, 320)
      })
    },

    setupResizeObserver() {
      this.teardownResizeObserver()
      if (typeof ResizeObserver === 'undefined') return

      const target = this.$refs.resizeHost || this.$el
      if (!target) return

      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleChartResize()
      })
      this.resizeObserver.observe(target)
    },

    teardownResizeObserver() {
      if (!this.resizeObserver) return
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    },

    parseMode(mode) {
      if (mode === undefined || mode === null) {
        return { modes: [] }
      }

      if (typeof mode === 'string' && mode.includes(',')) {
        const parts = mode.split(',')
          .map(part => Number(part.trim()))
          .filter(part => Number.isFinite(part))
          .map(part => Math.round(part))
          .sort((a, b) => a - b)
          .slice(0, 3)
        return { modes: parts }
      }

      if (typeof mode === 'number') {
        return { modes: [Math.round(mode)] }
      }

      if (Array.isArray(mode)) {
        const parts = mode
          .map(part => Number(part))
          .filter(part => Number.isFinite(part))
          .map(part => Math.round(part))
          .sort((a, b) => a - b)
          .slice(0, 3)
        return { modes: parts }
      }

      const numericMode = Number(mode)
      return { modes: Number.isFinite(numericMode) ? [Math.round(numericMode)] : [] }
    },

    processData(data) {
      if (!data || !data.message || !data.message.dayStatsRange || data.message.dayStatsRange.length === 0) {
        this.noData = true
        return
      }

      this.noData = false

      // Doble $nextTick: el primero permite que Vue renderice el div (v-else),
      // el segundo garantiza que el DOM esté disponible para ECharts
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (!this.$refs.chartTrends) return

          if (!this.chartInstance) {
            this.chartInstance = echarts.init(this.$refs.chartTrends)
          }

        const dayStatsRange = data.message.dayStatsRange

        const madrugadaBase = []
        const madrugadaSecond = []
        const madrugadaThird = []
        const diurnoBase = []
        const diurnoSecond = []
        const diurnoThird = []
        const nocturnoBase = []
        const nocturnoSecond = []
        const nocturnoThird = []
        const dateLabels = []

        dayStatsRange.forEach(day => {
          dateLabels.push(this.formatDate(day.fecha))

          const madrugada = day.grupos.find(g => g.grupo === 'Madrugada')
          const diurno = day.grupos.find(g => g.grupo === 'Diurno')
          const nocturno = day.grupos.find(g => g.grupo === 'Nocturno')

          const m = this.parseMode(madrugada && madrugada.mode).modes
          const d = this.parseMode(diurno && diurno.mode).modes
          const n = this.parseMode(nocturno && nocturno.mode).modes

          madrugadaBase.push(m[0] != null ? m[0] : null)
          madrugadaSecond.push(m[1] != null && m[0] != null ? Math.abs(m[1] - m[0]) : null)
          madrugadaThird.push(m[2] != null && m[1] != null ? Math.abs(m[2] - m[1]) : null)

          diurnoBase.push(d[0] != null ? d[0] : null)
          diurnoSecond.push(d[1] != null && d[0] != null ? Math.abs(d[1] - d[0]) : null)
          diurnoThird.push(d[2] != null && d[1] != null ? Math.abs(d[2] - d[1]) : null)

          nocturnoBase.push(n[0] != null ? n[0] : null)
          nocturnoSecond.push(n[1] != null && n[0] != null ? Math.abs(n[1] - n[0]) : null)
          nocturnoThird.push(n[2] != null && n[1] != null ? Math.abs(n[2] - n[1]) : null)
        })

        // Rango Y dinámico
        const allNumbers = [
          ...madrugadaBase, ...diurnoBase, ...nocturnoBase
        ].filter(v => v !== null)

        madrugadaBase.forEach((base, i) => {
          if (base !== null && madrugadaSecond[i] !== null) allNumbers.push(base + madrugadaSecond[i])
          if (base !== null && madrugadaSecond[i] !== null && madrugadaThird[i] !== null) {
            allNumbers.push(base + madrugadaSecond[i] + madrugadaThird[i])
          }
        })
        diurnoBase.forEach((base, i) => {
          if (base !== null && diurnoSecond[i] !== null) allNumbers.push(base + diurnoSecond[i])
          if (base !== null && diurnoSecond[i] !== null && diurnoThird[i] !== null) {
            allNumbers.push(base + diurnoSecond[i] + diurnoThird[i])
          }
        })
        nocturnoBase.forEach((base, i) => {
          if (base !== null && nocturnoSecond[i] !== null) allNumbers.push(base + nocturnoSecond[i])
          if (base !== null && nocturnoSecond[i] !== null && nocturnoThird[i] !== null) {
            allNumbers.push(base + nocturnoSecond[i] + nocturnoThird[i])
          }
        })

        const minY = allNumbers.length > 0 ? Math.min(...allNumbers) - 5 : 0

        const showLabels = dayStatsRange.length < 8

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: { fontSize: 11 },
            formatter: (params) => {
              const dateIndex = params[0].dataIndex
              let result = dateLabels[dateIndex] + '<br/>'

              params.forEach(p => {
                if (p.seriesName.includes('-second') || p.seriesName.includes('-third')) return
                if (p.value === null || p.value === undefined) return

                const baseVal = p.value
                const values = [baseVal]

                const secondSeries = params.find(s => s.seriesName === p.seriesName + '-second')
                if (secondSeries && secondSeries.value !== null && secondSeries.value !== undefined && secondSeries.value > 0) {
                  values.push(baseVal + secondSeries.value)
                }

                const thirdSeries = params.find(s => s.seriesName === p.seriesName + '-third')
                if (thirdSeries && thirdSeries.value !== null && thirdSeries.value !== undefined && thirdSeries.value > 0) {
                  values.push(baseVal + (secondSeries && secondSeries.value ? secondSeries.value : 0) + thirdSeries.value)
                }

                const line = p.seriesName + ': <strong>' + values.join(', ') + ' dBA</strong>'
                result += line + '<br/>'
              })
              return result
            }
          },
          legend: {
            data: ['Madrugada', 'Diurno', 'Nocturno'],
            top: 2,
            left: 'center',
            orient: 'horizontal',
            textStyle: { fontSize: 7 },
            itemWidth: 9,
            itemHeight: 7,
            itemGap: 10,
            formatter: (name) => {
              if (name === 'Madrugada') return 'Madrugada 00-07hrs'
              if (name === 'Diurno') return 'Diurno 07-21hrs'
              if (name === 'Nocturno') return 'Nocturno 21-00hrs'
              return ''
            }
          },
          toolbox: {
            top: 2,
            right: 0,
            itemSize: 11,
            iconStyle: {
              borderColor: '#374151'
            },
            emphasis: {
              iconStyle: {
                borderColor: '#111827'
              }
            },
            feature: {
              dataZoom: {
                yAxisIndex: 'none',
                title: {
                  zoom: 'Zoom',
                  back: 'Restablecer zoom'
                }
              },
              restore: {
                title: 'Inicio'
              },
              saveAsImage: {
                title: 'Descargar'
              }
            }
          },
          grid: {
            left: '3%',
            right: '3%',
            bottom: 16,
            top: 24,
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: dateLabels,
            axisLabel: {
              fontSize: 8,
              interval: 0,
              rotate: 45
            }
          },
          yAxis: {
            type: 'value',
            name: 'Tendencia (dBA)',
            nameLocation: 'middle',
            nameGap: 25,
            nameRotate: 90,
            nameTextStyle: {
              fontSize: 11,
              fontWeight: 'bold',
              color: '#000000'
            },
            min: minY,
            axisLabel: { fontSize: 9 }
          },
          series: [
            // Madrugada base
            {
              name: 'Madrugada',
              type: 'bar',
              stack: 'Madrugada',
              data: madrugadaBase,
              itemStyle: { color: '#ADD8E6', borderWidth: 1 },
              barGap: '5%',
              barCategoryGap: '20%',
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold'
              }
            },
            // Madrugada second
            {
              name: 'Madrugada-second',
              type: 'bar',
              stack: 'Madrugada',
              data: madrugadaSecond,
              itemStyle: { color: 'rgba(99, 142, 163, 0.85)', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (madrugadaBase[params.dataIndex] + params.value) + ''
                }
              },
              legendHoverLink: false
            },
            // Diurno base
            {
              name: 'Diurno',
              type: 'bar',
              stack: 'Diurno',
              data: diurnoBase,
              itemStyle: { color: '#FFA500', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold'
              }
            },
            // Diurno second
            {
              name: 'Diurno-second',
              type: 'bar',
              stack: 'Diurno',
              data: diurnoSecond,
              itemStyle: { color: 'rgba(204, 132, 0, 0.85)', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (diurnoBase[params.dataIndex] + params.value) + ''
                }
              },
              legendHoverLink: false
            },
            // Nocturno base
            {
              name: 'Nocturno',
              type: 'bar',
              stack: 'Nocturno',
              data: nocturnoBase,
              itemStyle: { color: '#4682B4', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold'
              }
            },
            // Nocturno second
            {
              name: 'Nocturno-second',
              type: 'bar',
              stack: 'Nocturno',
              data: nocturnoSecond,
              itemStyle: { color: '#1e3a5f', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (nocturnoBase[params.dataIndex] + params.value) + ''
                }
              },
              legendHoverLink: false
            },
            {
              name: 'Madrugada-third',
              type: 'bar',
              stack: 'Madrugada',
              data: madrugadaThird,
              itemStyle: { color: '#0d1f2d', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (madrugadaBase[params.dataIndex] + (madrugadaSecond[params.dataIndex] || 0) + params.value) + ''
                }
              },
              legendHoverLink: false
            },
            {
              name: 'Diurno-third',
              type: 'bar',
              stack: 'Diurno',
              data: diurnoThird,
              itemStyle: { color: '#663300', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (diurnoBase[params.dataIndex] + (diurnoSecond[params.dataIndex] || 0) + params.value) + ''
                }
              },
              legendHoverLink: false
            },
            {
              name: 'Nocturno-third',
              type: 'bar',
              stack: 'Nocturno',
              data: nocturnoThird,
              itemStyle: { color: '#051018', borderWidth: 1 },
              label: {
                show: showLabels,
                position: 'inside',
                fontSize: 8,
                color: '#fff',
                fontWeight: 'bold',
                formatter: (params) => {
                  if (!params.value) return ''
                  return (nocturnoBase[params.dataIndex] + (nocturnoSecond[params.dataIndex] || 0) + params.value) + ''
                }
              },
              legendHoverLink: false
            }
          ]
        }

        this.chartInstance.setOption(option)
        this.scheduleChartResize()
        })
      })
    },

    formatDate(dateString) {
      const parts = dateString.split('-')
      if (parts.length === 3) {
        const day = parts[0]
        const month = parts[1]
        return `${day}/${month}`
      }
      return dateString
    }
  }
}
</script>
<style scoped>
.chart-card {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chart-title {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  line-height: 1.1;
  padding-top: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  color: #000000 !important;
}

.chart-body {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 0 !important;
  margin-top: 4px;
}

.chart-loading-shell {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.chart-skeleton-fill {
  flex: 1 1 auto;
  height: 100% !important;
}

.chart-skeleton-fill >>> .v-skeleton-loader__image {
  height: 100% !important;
  margin: 0 !important;
}

.range-trend-chart__canvas {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;
}
</style>
